import React, { useState, useEffect, useContext, useRef } from "react";
import { GetPayLoad } from "../services/PayLoadStorage";
import { LanguageContext } from "../contexts/LanguageContext";
import Cookies from 'js-cookie';

const AutoReadPage = ({ pageTitle = "OverView" ,contentFromExternal=null}) => {
  
  //This is Localstorage value
  const Payload = GetPayLoad('PayLoad');
  let owningCarrier = Payload?.owning_carrier || '';
  const isConnectiCareCarrier = Cookies.get("isConnectiCareCarrier") || 'false';

  // Refs for 508 compliance announcements
  const urlAnnouncementRef = useRef(null);
  const logoAnnouncementRef = useRef(null);
  const menuAnnouncementRef = useRef(null);
  const langDropdownRef = useRef(null);
  const topSectionRef = useRef(null);
  const bottomMenuRef = useRef(null);
  const [readingSequenceStep, setReadingSequenceStep] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const { pageContent } = useContext(LanguageContext);
  const [genericContent, setGenericContent] = useState([]);
  
  useEffect(() => {
    if (pageContent?.generic) {
        setGenericContent(pageContent.generic);
    } else if (contentFromExternal.generic) {
        setGenericContent(contentFromExternal.generic);
    }
  }, [pageContent, contentFromExternal]);

  // Detect user interaction to stop auto-reading
  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    
    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // st48236 - 508 compliance: Check if page has already been announced to avoid duplicates
  useEffect(() => {
    const pageKey = `autoReadComplete_${window.location.pathname}`;
    const hasAlreadyRead = Cookies.get(pageKey);
    
    if (hasAlreadyRead) {
      // Skip announcements if already read on this page
      setReadingSequenceStep(6);
      return;
    }
  }, []);

  // st48236 - 508 compliance: Reading sequence - URL announcement
  useEffect(() => {
    if (readingSequenceStep === 0 && urlAnnouncementRef.current) {
        urlAnnouncementRef.current.textContent = `Current URL is ${window.location.href}`;
        urlAnnouncementRef.current.setAttribute('aria-live', 'assertive');
        setReadingSequenceStep(1);
    }
  }, [readingSequenceStep]);

  // st48236 - 508 compliance: Reading sequence - Logo announcement
  useEffect(() => {
    let timeoutId;
    
    if (!userInteracted && readingSequenceStep === 1 && logoAnnouncementRef.current) {
        timeoutId = setTimeout(() => {
            if(owningCarrier === 'J1'){
                logoAnnouncementRef.current.textContent = `Cigna logo loaded.`;
            }
            else if(owningCarrier === 'M1'){
                logoAnnouncementRef.current.textContent = `MHC logo loaded.`;
            }
            else if(owningCarrier === 'Q5'){
                logoAnnouncementRef.current.textContent = `BCBSAZ logo loaded.`;
            }
            else if(owningCarrier === 'SY'){
                logoAnnouncementRef.current.textContent = `Sentara logo loaded.`;
            }
            else if(owningCarrier === 'XC'){
                logoAnnouncementRef.current.textContent = `Christus logo loaded.`;
            }
            else if(owningCarrier === 'C4'){
                if(isConnectiCareCarrier === 'true'){
                    logoAnnouncementRef.current.textContent = `ConnectiCare logo loaded.`;
                }else{
                    logoAnnouncementRef.current.textContent = `Molina logo loaded.`;
                }
            }
            setReadingSequenceStep(2);
        }, 0);
    }
    
    return () => {
        if (timeoutId) clearTimeout(timeoutId);
    };
  }, [readingSequenceStep, userInteracted, owningCarrier]);

  // st48236 - 508 compliance: Reading sequence - Side menu announcement
  useEffect(() => {
    let timeoutId;    
    if (!userInteracted && readingSequenceStep === 2 && menuAnnouncementRef.current && genericContent) {
        timeoutId = setTimeout(() => {
            // Guard until genericContent is a non-empty object
            if (!genericContent || Array.isArray(genericContent) || Object.keys(genericContent).length === 0) {
                console.log('genericContent not ready yet', genericContent);
                return;
            }
            const coverageText = owningCarrier === 'Q5'?'Policy':'Coverage'
           
            const menuItems = [
                genericContent?.sidebar_menu_overview_text ?? 'Overview',
                genericContent?.sidebar_menu_billing_text ?? 'Billing',
                genericContent?.sidebar_menu_coverage_text ?? coverageText,
                genericContent?.sidebar_menu_edelivery_text ?? 'E-delivery',
                genericContent?.sidebar_menu_settings_text ?? 'Settings'
            ];
            
            const menuAnnouncement = `Sidebar menu items: ${menuItems.join(', ')}`;
            if (menuAnnouncementRef.current) {
                menuAnnouncementRef.current.textContent = menuAnnouncement;
            }
            setReadingSequenceStep(3);
        }, 0);
    }
    
    return () => {
        if (timeoutId) clearTimeout(timeoutId);
    };
  }, [readingSequenceStep, userInteracted, genericContent]);

  // st48236 - 508 compliance: Reading sequence - Language dropdown announcement
  useEffect(() => {
    let timeoutId;
    
    if (!userInteracted && readingSequenceStep === 3 && langDropdownRef.current && genericContent) {
         // Language array with key-value pairs
        const languageArray = [
            { key: 'eng', value: genericContent?.language_option_english ?? 'English' },
            { key: 'spa', value: genericContent?.language_option_spanish ?? 'Spanish' }
        ];
        timeoutId = setTimeout(() => {
            try {
                const langLabel = 'Language selection';
                const languageSelect = document.getElementById('language');
                let langAnnouncement = '';
                
                if (languageSelect && languageSelect.options && languageSelect.options.length > 0) {
                    const langOptions = Array.from(languageSelect.options).map(opt => opt.text || opt.label || opt.value);
                    const cookieLangKey = Cookies.get('lang');
                    const selectedOption =
                        languageSelect?.options?.[languageSelect.selectedIndex]?.text ||
                        (languageArray.find(l => l.key === cookieLangKey)?.value) ||
                        'English';
                    langAnnouncement = `${langLabel}: ${languageArray.map(lang => lang.value).join(', ')} available. Currently selected language is ${selectedOption}.`;
                } else {
                    langAnnouncement = `${langLabel} not found or not available yet`;
                    console.warn('Language dropdown element not found or has no options');
                }
                
                if (langDropdownRef.current) {
                    langDropdownRef.current.textContent = langAnnouncement;
                }
            } catch (error) {
                console.error('Error reading language dropdown:', error);
                if (langDropdownRef.current) {
                    langDropdownRef.current.textContent = 'Language selection available';
                }
            }
            setReadingSequenceStep(4);
        }, 0);  // Increased delay to ensure DOM is ready after login
    }
    
    return () => {
        if (timeoutId) clearTimeout(timeoutId);
    };
  }, [readingSequenceStep, userInteracted, genericContent]);

  // st48236 - 508 compliance: Reading sequence - Top section (Profile menu) announcement
  useEffect(() => {
    let timeoutId;
    
    if (!userInteracted && readingSequenceStep === 4 && topSectionRef.current) {
        timeoutId = setTimeout(() => {
            const menuContainer = document.getElementById('payload-dropdown-menu');
            const navbarEl = document.getElementById('navbarDropdown');
            let profileMenuAnnouncement = 'Profile menu ';
            
            if (navbarEl) {
                const label = navbarEl.getAttribute('aria-label') || navbarEl.title || navbarEl.textContent || navbarEl.innerText;
                profileMenuAnnouncement += (label || 'Profile menu').trim();
            }
            
            if (menuContainer) {
                const itemNodes = menuContainer.querySelectorAll('[role="menuitem"], li, button, a');
                const items = Array.from(itemNodes)
                    .map(n => (n.getAttribute('aria-label') || n.textContent || '').trim())
                    .filter(txt => txt.length > 0);
                if (items.length > 0) {
                    const uniqueItems = Array.from(new Set(items));
                    profileMenuAnnouncement += `: ${uniqueItems.join(', ')}`;
                } else {
                    const menuItems = [
                        genericContent?.header_account_settings_menu ?? 'Account Settings',
                        genericContent?.header_logout_menu ?? 'Logout'
                    ];
                    if (menuItems.length > 0) {
                      profileMenuAnnouncement += `: ${menuItems.join(', ')}`;
                    } else {
                      profileMenuAnnouncement += ': no menu found.';
                    }
                }
            }
            
            if (topSectionRef.current) {
                topSectionRef.current.textContent = profileMenuAnnouncement;
            }
            setReadingSequenceStep(5);
        }, 0);
    }
    
    return () => {
        if (timeoutId) clearTimeout(timeoutId);
    };
  }, [readingSequenceStep, userInteracted, genericContent]);

  // st48236 - 508 compliance: Reading sequence - Hamburger menu announcement
  useEffect(() => {
    let timeoutId;
    
    if (!userInteracted && readingSequenceStep === 5 && bottomMenuRef.current) {
        timeoutId = setTimeout(() => {
            let bottomMenuAnnouncement = 'Hamburger menu: ';
            const menuItems = [
                genericContent?.footer_limited_eng_prof_menu || 'Limited English Proficiency',
                genericContent?.footer_non_disc_menu || 'Non Discrimination Notice',
                genericContent?.layout_contact_us_page_title || 'Contact Us',
                genericContent?.footer_terms_menu || 'Terms of Use',
                genericContent?.layout_faq_page_title || "FAQ's",
                genericContent?.footer_glossary_menu || 'Glossary & Accronyms',
                genericContent?.footer_privacy_menu ?? ''
            ];
            if (menuItems.length > 0) {
                bottomMenuAnnouncement += `: ${menuItems.join(', ')}`;
            } else {
                bottomMenuAnnouncement += ': no menu found.';
            }
            
            if (bottomMenuRef.current) {
                bottomMenuRef.current.textContent = bottomMenuAnnouncement;
            }
            setReadingSequenceStep(6);
        }, 0);
    }
    
    return () => {
        if (timeoutId) clearTimeout(timeoutId);
    };
  }, [readingSequenceStep, userInteracted]);

  // st48236 - 508 compliance: Mark reading as complete and store flag
  useEffect(() => {
    if (readingSequenceStep === 6) {
      const pageKey = `autoReadComplete_${window.location.pathname}`;
      Cookies.set(pageKey, 'true', { expires: 1 }); // Expires at end of day
    }
  }, [readingSequenceStep]);

  return (    
    <>
      {/* 508 compliance announcements - Hidden from view */}
      <div ref={urlAnnouncementRef} aria-live="assertive" role="status" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} tabIndex={0}></div>
      <div ref={logoAnnouncementRef} aria-live="assertive" role="status" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} tabIndex={0}></div>
      <div ref={menuAnnouncementRef} aria-live="assertive" role="status" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} tabIndex={0}></div>
      <div ref={langDropdownRef} aria-live="assertive" role="status" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} tabIndex={0}></div>
      <div ref={topSectionRef} aria-live="assertive" role="status" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} tabIndex={0}></div>
      <div ref={bottomMenuRef} aria-live="assertive" role="status" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} tabIndex={0}></div>
    </>
  );
};

export default AutoReadPage;
